import {
  InterviewObservabilityContext,
  interviewPerformanceNow,
  trackInterviewDuration,
  trackInterviewMetric,
} from './interviewObservability';

export type InterviewQuestionDeliveryState =
  | 'authoritative'
  | 'delivering'
  | 'recovery'
  | 'delivered'
  | 'interrupted'
  | 'cancelled';

export type InterviewQuestionDeliverySnapshot = {
  state: InterviewQuestionDeliveryState;
  deliveryId: string | null;
  attempt: number;
  waitMs: number | null;
};

export type InterviewQuestionDeliveryPolicy = {
  recoveryWindowMs: number;
  retryDelaysMs: number[];
  retryMaxDelayMs: number;
  initialRequestTimeoutMs: number;
  playbackAttemptTimeoutMs: number;
};

export const INTERVIEW_QUESTION_DELIVERY_POLICY: InterviewQuestionDeliveryPolicy = {
  recoveryWindowMs: 30000,
  retryDelaysMs: [500, 1000, 2000, 4000, 8000],
  retryMaxDelayMs: 8000,
  initialRequestTimeoutMs: 10000,
  playbackAttemptTimeoutMs: 30000,
};

type BrowserEventTarget = Pick<Window, 'addEventListener' | 'removeEventListener'>;

type DeliveryTask<T> = {
  deliveryId: string;
  operation: (
    attempt: number,
    requestTimeoutMs: number,
    remainingRequestTimeoutMs: () => number,
    signal: AbortSignal,
  ) => Promise<T>;
  observability?: InterviewObservabilityContext;
  onStateChange?: (snapshot: InterviewQuestionDeliverySnapshot) => void;
  attempt: number;
  startedAt: number;
  recoveryStartedAt: number | null;
  attemptAbortController: AbortController | null;
  cancelled: boolean;
  promise: Promise<T> | null;
};

export class InterviewQuestionDeliveryFailure extends Error {
  exhausted: boolean;

  originalError: unknown;

  constructor(message: string, originalError: unknown, exhausted = false) {
    super(message);
    this.name = 'InterviewQuestionDeliveryFailure';
    this.exhausted = exhausted;
    this.originalError = originalError;
  }
}

export class InterviewQuestionDeliveryCancelledError extends Error {
  constructor() {
    super('Question delivery cancelled');
    this.name = 'InterviewQuestionDeliveryCancelledError';
  }
}

export class InterviewQuestionDeliveryAttemptTimeoutError extends Error {
  constructor() {
    super('Question delivery attempt exceeded its playback deadline.');
    this.name = 'InterviewQuestionDeliveryAttemptTimeoutError';
  }
}

const normalizeDelay = (value: unknown, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed) : fallback;
};

const clonePolicy = (policy?: Partial<InterviewQuestionDeliveryPolicy>): InterviewQuestionDeliveryPolicy => ({
  ...INTERVIEW_QUESTION_DELIVERY_POLICY,
  ...policy,
  retryDelaysMs: Array.isArray(policy?.retryDelaysMs)
    ? policy!.retryDelaysMs.map((delay) => normalizeDelay(delay, 0))
    : [...INTERVIEW_QUESTION_DELIVERY_POLICY.retryDelaysMs],
});

export const buildAuthoritativeQuestionDeliveryId = ({
  interviewId,
  questionId,
  questionType,
  questionIndex,
}: {
  interviewId: number;
  questionId: number;
  questionType?: string | null;
  questionIndex?: number | null;
}) => [
  `iv-${Number(interviewId) || 0}`,
  `q-${Number(questionId) || 0}`,
  `type-${String(questionType || 'question').toLowerCase()}`,
  `rev-${Number.isFinite(Number(questionIndex)) ? Number(questionIndex) : 0}`,
].join(':');

export class InterviewQuestionDeliveryCoordinator {
  private readonly policy: InterviewQuestionDeliveryPolicy;

  private readonly onlineTarget: BrowserEventTarget | null;

  private activeTask: DeliveryTask<any> | null = null;

  private waitTimer: ReturnType<typeof setTimeout> | null = null;

  private onlineListener: (() => void) | null = null;

  private waitResolve: ((source: 'timer' | 'online') => void) | null = null;

  private snapshot: InterviewQuestionDeliverySnapshot = {
    state: 'authoritative',
    deliveryId: null,
    attempt: 0,
    waitMs: null,
  };

  constructor(options: {
    policy?: Partial<InterviewQuestionDeliveryPolicy>;
    onlineTarget?: BrowserEventTarget | null;
  } = {}) {
    this.policy = clonePolicy(options.policy);
    this.onlineTarget = options.onlineTarget === undefined
      ? (typeof window !== 'undefined' ? window : null)
      : options.onlineTarget;
  }

  getSnapshot() {
    return { ...this.snapshot };
  }

  execute<T>({
    deliveryId,
    operation,
    observability,
    onStateChange,
  }: {
    deliveryId: string;
    operation: (
      attempt: number,
      requestTimeoutMs: number,
      remainingRequestTimeoutMs: () => number,
      signal: AbortSignal,
    ) => Promise<T>;
    observability?: InterviewObservabilityContext;
    onStateChange?: (snapshot: InterviewQuestionDeliverySnapshot) => void;
  }): Promise<T> {
    if (this.activeTask) {
      if (this.activeTask.deliveryId === deliveryId && this.activeTask.promise) {
        return this.activeTask.promise as Promise<T>;
      }
      return Promise.reject(new InterviewQuestionDeliveryFailure(
        'Authoritative question delivery is already active.',
        null,
      ));
    }

    const task: DeliveryTask<T> = {
      deliveryId,
      operation,
      observability,
      onStateChange,
      attempt: 0,
      startedAt: interviewPerformanceNow(),
      recoveryStartedAt: null,
      attemptAbortController: null,
      cancelled: false,
      promise: null,
    };
    this.activeTask = task;
    task.promise = this.run(task).finally(() => {
      if (this.activeTask === task) this.activeTask = null;
    });
    return task.promise;
  }

  cancel() {
    if (!this.activeTask) return;
    this.activeTask.cancelled = true;
    this.activeTask.attemptAbortController?.abort();
    this.finishWait('timer');
  }

  private updateSnapshot(task: DeliveryTask<any>, state: InterviewQuestionDeliveryState, waitMs: number | null = null) {
    this.snapshot = {
      state,
      deliveryId: task.deliveryId,
      attempt: task.attempt,
      waitMs,
    };
    task.onStateChange?.({ ...this.snapshot });
  }

  private metricPayload(task: DeliveryTask<any>, details: Record<string, any> = {}) {
    return {
      ...task.observability,
      recoveryId: task.deliveryId,
      requestAttempt: task.attempt,
      ...details,
    };
  }

  private async run<T>(task: DeliveryTask<T>): Promise<T> {
    let retryIndex = 0;
    let lastError: unknown = null;
    trackInterviewMetric('question_delivery_started', this.metricPayload(task, {
      outcome: 'started',
    }));

    while (true) {
      if (task.cancelled) {
        this.updateSnapshot(task, 'cancelled');
        throw new InterviewQuestionDeliveryCancelledError();
      }

      const now = interviewPerformanceNow();
      const remainingRecoveryMs = task.recoveryStartedAt === null
        ? this.policy.recoveryWindowMs
        : Math.max(0, this.policy.recoveryWindowMs - (now - task.recoveryStartedAt));
      if (task.recoveryStartedAt !== null && remainingRecoveryMs <= 0) {
        this.updateSnapshot(task, 'interrupted');
        trackInterviewDuration('question_delivery_exhausted', task.recoveryStartedAt, this.metricPayload(task, {
          outcome: 'window_exhausted',
        }));
        throw new InterviewQuestionDeliveryFailure(
          'Question delivery recovery exhausted.',
          lastError,
          true,
        );
      }

      task.attempt += 1;
      this.updateSnapshot(task, 'delivering');
      if (task.attempt > 1) {
        trackInterviewMetric('question_delivery_retry', this.metricPayload(task, {
          outcome: 'started',
        }));
      }

      try {
        const attemptTimeoutMs = Math.max(1, Math.min(
          normalizeDelay(this.policy.playbackAttemptTimeoutMs, this.policy.recoveryWindowMs),
          Math.round(remainingRecoveryMs),
        ));
        const attemptDeadlineAt = now + attemptTimeoutMs;
        const requestTimeoutMs = Math.max(1, Math.min(
          this.policy.initialRequestTimeoutMs,
          attemptTimeoutMs,
        ));
        const remainingRequestTimeoutMs = () => {
          const remainingAttemptMs = Math.max(1, Math.round(
            attemptDeadlineAt - interviewPerformanceNow()
          ));
          if (task.recoveryStartedAt === null) {
            return Math.max(1, Math.min(this.policy.initialRequestTimeoutMs, remainingAttemptMs));
          }
          return Math.max(1, Math.min(
            this.policy.initialRequestTimeoutMs,
            remainingAttemptMs,
            Math.round(this.policy.recoveryWindowMs - (
              interviewPerformanceNow() - Number(task.recoveryStartedAt)
            )),
          ));
        };
        const result = await this.runBoundedAttempt(
          task,
          requestTimeoutMs,
          remainingRequestTimeoutMs,
          attemptTimeoutMs,
        );
        if (task.cancelled) throw new InterviewQuestionDeliveryCancelledError();
        this.updateSnapshot(task, 'delivered');
        trackInterviewDuration('question_delivery_completed', task.startedAt, this.metricPayload(task, {
          outcome: task.attempt === 1 ? 'completed' : 'recovered',
        }));
        if (task.recoveryStartedAt !== null) {
          trackInterviewDuration('question_delivery_recovered', task.recoveryStartedAt, this.metricPayload(task, {
            outcome: 'recovered',
          }));
        }
        return result;
      } catch (error) {
        if (error instanceof InterviewQuestionDeliveryCancelledError || task.cancelled) {
          this.updateSnapshot(task, 'cancelled');
          throw new InterviewQuestionDeliveryCancelledError();
        }
        lastError = error;
        trackInterviewMetric('question_delivery_failed', this.metricPayload(task, {
          outcome: 'failed',
        }));
        if (task.recoveryStartedAt === null) {
          task.recoveryStartedAt = interviewPerformanceNow();
          trackInterviewMetric('question_delivery_recovery_started', this.metricPayload(task, {
            outcome: 'started',
          }));
        }

        const elapsed = interviewPerformanceNow() - task.recoveryStartedAt;
        const remainingRecoveryMs = Math.max(0, this.policy.recoveryWindowMs - elapsed);
        if (remainingRecoveryMs <= 0) continue;
        const configuredDelay = this.policy.retryDelaysMs[
          Math.min(retryIndex, Math.max(0, this.policy.retryDelaysMs.length - 1))
        ];
        const waitMs = Math.min(
          normalizeDelay(configuredDelay, this.policy.retryMaxDelayMs),
          normalizeDelay(this.policy.retryMaxDelayMs, 8000),
          remainingRecoveryMs,
        );
        retryIndex += 1;
        this.updateSnapshot(task, 'recovery', waitMs);
        await this.wait(waitMs, true);
      }
    }
  }

  private runBoundedAttempt<T>(
    task: DeliveryTask<T>,
    requestTimeoutMs: number,
    remainingRequestTimeoutMs: () => number,
    attemptTimeoutMs: number,
  ) {
    const abortController = new AbortController();
    task.attemptAbortController = abortController;

    return new Promise<T>((resolve, reject) => {
      let settled = false;
      let watchdog: ReturnType<typeof setTimeout> | null = null;

      const handleAbort = () => {
        settleFailure(new InterviewQuestionDeliveryCancelledError());
      };
      const clearWatchdog = () => {
        if (watchdog !== null) {
          clearTimeout(watchdog);
          watchdog = null;
        }
        abortController.signal.removeEventListener('abort', handleAbort);
        if (task.attemptAbortController === abortController) {
          task.attemptAbortController = null;
        }
      };
      const settleSuccess = (value: T) => {
        if (settled) return;
        settled = true;
        clearWatchdog();
        resolve(value);
      };
      const settleFailure = (error: unknown) => {
        if (settled) return;
        settled = true;
        clearWatchdog();
        reject(error);
      };

      abortController.signal.addEventListener('abort', handleAbort, { once: true });
      watchdog = setTimeout(() => {
        if (settled) return;
        settled = true;
        clearWatchdog();
        abortController.abort();
        reject(new InterviewQuestionDeliveryAttemptTimeoutError());
      }, attemptTimeoutMs);

      try {
        const operation = task.operation(
          task.attempt,
          requestTimeoutMs,
          remainingRequestTimeoutMs,
          abortController.signal,
        );
        Promise.resolve(operation).then(settleSuccess, settleFailure);
      } catch (error) {
        settleFailure(error);
      }
    });
  }

  private wait(waitMs: number, allowOnlineWake: boolean) {
    return new Promise<'timer' | 'online'>((resolve) => {
      let settled = false;
      const finish = (source: 'timer' | 'online') => {
        if (settled) return;
        settled = true;
        if (this.waitTimer !== null) {
          clearTimeout(this.waitTimer);
          this.waitTimer = null;
        }
        if (this.onlineListener && this.onlineTarget) {
          this.onlineTarget.removeEventListener('online', this.onlineListener);
          this.onlineListener = null;
        }
        this.waitResolve = null;
        resolve(source);
      };
      this.waitResolve = finish;
      this.waitTimer = setTimeout(() => finish('timer'), waitMs);
      if (allowOnlineWake && this.onlineTarget) {
        this.onlineListener = () => finish('online');
        this.onlineTarget.addEventListener('online', this.onlineListener);
      }
    });
  }

  private finishWait(source: 'timer' | 'online') {
    this.waitResolve?.(source);
  }
}

export const createInterviewQuestionDeliveryCoordinator = (
  options: ConstructorParameters<typeof InterviewQuestionDeliveryCoordinator>[0] = {},
) => new InterviewQuestionDeliveryCoordinator(options);

export const isInterviewQuestionDeliveryCancelled = (error: unknown) => (
  error instanceof InterviewQuestionDeliveryCancelledError
);
